/**
 * A ata como o cliente a lê: o resumo, as decisões e as pendências dele. Nada além.
 *
 * **Ela não recorta nada, e mesmo assim filtra.** O recorte é do servidor — `visibilidade.py` e,
 * antes dele, a policy `ata_item_isolamento` da `0013`, que faz uma nota interna **não existir**
 * para a sessão do cliente. O que chega aqui já veio cortado. O filtro por `destinoDoItem` na
 * lista abaixo é cinto e suspensório de propósito: se um dia a fronteira do servidor falhasse, a
 * tela do cliente não é onde a falha deve aparecer. Custa uma linha e cobre o pior caso da fatia.
 *
 * **Rascunho não chega aqui, e não é esta tela que impede.** Para o cliente, uma ata em rascunho
 * responde `404` — a mesma resposta de uma reunião sem ata. Distinguir as duas contaria a ele que
 * alguém escreveu algo sobre a reunião dele que ele não pode ler, e contar isso já é atravessar
 * metade da fronteira.
 *
 * O mesmo componente serve a tela do cliente e a leitura interna de uma ata publicada, porque o
 * que ele desenha é o que atravessou — e é bom que quem publicou veja exatamente essa página.
 */

import { Aviso, Cartao, Chip } from "../ui";
import {
  ROTULO_DO_LADO,
  destinoDoItem,
  formatarPrazo,
  formatarQuando,
  type Ata,
  type ItemDaAta,
} from "../../nucleo/reuniao";

export interface PropsDaAtaPublicada {
  ata: Ata;
  /** Título do cartão — normalmente o tipo e a data da reunião. */
  titulo: string;
  "data-testid"?: string;
}

export function AtaPublicada({ ata, titulo, ...resto }: PropsDaAtaPublicada) {
  const itens = ata.itens.filter((item) => destinoDoItem(item) === "cliente");
  const decisoes = itens.filter((item) => item.tipo === "decisao");
  const pendencias = itens.filter((item) => item.tipo === "pendencia");
  /* Qualquer coisa que não seja decisão nem pendência e mesmo assim tenha atravessado continua
     sendo mostrada, em vez de sumir em silêncio: um item que a tela não sabe agrupar é melhor
     visível do que engolido — quem o escreveu decidiu que ele sai. */
  const outros = itens.filter(
    (item) => item.tipo !== "decisao" && item.tipo !== "pendencia",
  );

  return (
    <Cartao
      className="ata-publicada"
      titulo={titulo}
      acessorio={<Chip estado="ok">ata publicada</Chip>}
      {...resto}
    >
      <p className="nota-rodape mb-3">
        Publicada em{" "}
        <span className="numero-tabular">{formatarQuando(ata.publicado_em) ?? "—"}</span>. Este é o
        registro do que foi combinado — se algo aqui não bate com o que você entendeu, fale com o
        consultor antes que vire configuração.
      </p>

      {ata.resumo ? (
        <p className="ata-publicada__resumo" data-testid="resumo-publicado">
          {ata.resumo}
        </p>
      ) : null}

      <GrupoDeItens titulo="Decisões" itens={decisoes} />
      <GrupoDeItens titulo="Pendências" itens={pendencias} />
      <GrupoDeItens titulo="Registro" itens={outros} />

      {itens.length === 0 ? (
        <Aviso tom="marca" className="mt-3" data-testid="ata-so-com-resumo">
          Esta ata não tem itens além do resumo acima.
        </Aviso>
      ) : null}
    </Cartao>
  );
}

function GrupoDeItens({ titulo, itens }: { titulo: string; itens: readonly ItemDaAta[] }) {
  if (itens.length === 0) return null;

  return (
    <div className="ata-publicada__grupo">
      <p className="ata-publicada__titulo-do-grupo">{titulo}</p>
      <ul className="ata-publicada__lista">
        {itens.map((item) => (
          <li
            key={item.id}
            className="ata-publicada__item"
            data-tipo={item.tipo}
            data-testid={`item-publicado-${item.id}`}
          >
            {item.texto}
            {item.responsavel || item.prazo || item.responsavel_lado ? (
              <span className="ata-publicada__meta">
                {item.responsavel ? <b>{item.responsavel}</b> : null}
                {item.responsavel_lado
                  ? `${item.responsavel ? " · " : ""}${ROTULO_DO_LADO[item.responsavel_lado]}`
                  : ""}
                {item.prazo ? (
                  <>
                    {" · até "}
                    <span className="numero-tabular">{formatarPrazo(item.prazo)}</span>
                  </>
                ) : null}
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}
